import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { getPO, getBanks } from '../api';
import { alertError } from '../utils';
import { MODES } from '../config';

export const EMPTY_FORM = {
  expenseTypeId: 0,
  targetAccountNumber: '',
  targetAccountName: '',
  targetBankName: '',
  targetBranch: '',
  dueDate: null,
  periodStart: null,
  periodEnd: null,
  branchId: 0,
  remarks: '',
};

const EMPTY_BANK = { id: 0, accountName: '', bankName: '', accountNumber: '' };

const toDate = (value) => (value ? moment(value).toDate() : null);

const formFromPo = (po) => ({
  expenseTypeId: po.masterTujuanPengeluaranId,
  targetAccountNumber: po.nomorRekeningTujuan,
  targetAccountName: po.namaRekeningTujuan,
  targetBankName: po.namaBankTujuan,
  targetBranch: po.cabangTujuan,
  dueDate: toDate(po.dueDate),
  periodStart: toDate(po.periodStart),
  periodEnd: toDate(po.periodEnd),
  branchId: po.branchId,
  remarks: po.remarks,
});

/**
 * State for the PO Data tab, driven by `selection` ({ id, mode } or null):
 *  - `po`: the loaded header (code, tglPO, totalPrice, poItems, ...)
 *  - `form`: the editable header fields
 *  - `bank` / `mutationDate`: the finance row, hydrated when the mode says so
 * `reloadItems` refreshes `po` only (items + total) and keeps typed edits;
 * `reloadAll` re-hydrates the form from the server as well.
 */
export default function usePoDetail(selection) {
  const mode = MODES[selection?.mode ?? 'create'];

  const [po, setPo] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [bank, setBank] = useState(EMPTY_BANK);
  const [mutationDate, setMutationDate] = useState(null);
  const [banks, setBanks] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    getBanks().then(setBanks).catch(alertError);
  }, []);

  const reset = useCallback(() => {
    setPo(null);
    setForm(EMPTY_FORM);
    setBank(EMPTY_BANK);
    setMutationDate(null);
    setShowErrors(false);
  }, []);

  const reloadAll = useCallback(async () => {
    if (!selection) return;
    try {
      const data = await getPO(selection.id);
      setPo(data);
      setForm(formFromPo(data));
      const { hydrateBank } = MODES[selection.mode];
      setBank(hydrateBank ? { ...EMPTY_BANK, id: data.bankId } : EMPTY_BANK);
      setMutationDate(hydrateBank ? toDate(data.tanggalMutasi) : null);
    } catch (err) {
      alertError(err);
    }
  }, [selection]);

  const reloadItems = useCallback(async () => {
    if (!selection) return;
    try {
      setPo(await getPO(selection.id));
    } catch (err) {
      alertError(err);
    }
  }, [selection]);

  useEffect(() => {
    if (selection) reloadAll();
    else reset();
  }, [selection, reloadAll, reset]);

  const setField = useCallback(
    (key, value) => setForm((current) => ({ ...current, [key]: value })),
    [],
  );

  return {
    mode,
    po,
    form,
    setField,
    setForm,
    bank,
    setBank,
    mutationDate,
    setMutationDate,
    banks,
    showErrors,
    setShowErrors,
    reset,
    reloadAll,
    reloadItems,
  };
}
